/*
 * @providesModule PlayerLevelUpModal
 *
 */

import React from 'react';

import {_m} from 'INTL';
import CircleProgressLevel from 'CircleProgressLevel';
import HeaderModal from 'HeaderModal';
import Label from 'Label';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import OrientationTransitionController from 'OrientationTransitionController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import PropTypes from 'prop-types';
import PushButton from 'PushButton';
import SKZConstants from 'SKZConstants';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZPlayerLevel from 'SKZPlayerLevel';
import makeStyle from 'PlayerLevelUpModalStyle';

import {BackHandler, Platform, View} from 'react-native';

export const PLAYER_LEVEL_UP_TEST_IDS = {
  UNDEFINED_LEVEL: 'undefined_level',
  SKILLZ_LEVEL: 'skillz_level',
  GAME_LEVEL: 'game_level',
  TITLE: 'title',
  NEXT: 'next',
  LEARN_MORE: 'learn_more',
  BODY: 'body',
};

const {btnGradientOne, btnGradientTwo, viewCornerRadiusPrimary} = SkillzStyle.fullStyles;

const TABLET_SIZE = Sizes.isLandscape() ? 160 : 180; // eslint-disable-line no-magic-numbers

export default class PlayerLevelUpModal extends React.Component {
  static propTypes = {
    body: PropTypes.string,
    level: PropTypes.instanceOf(SKZPlayerLevel).isRequired,
    onLearnMore: PropTypes.func,
    onNext: PropTypes.func,
    shouldPlayConfettiOnAndroid: PropTypes.bool,
    showLearnMore: PropTypes.bool,
    subtitle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shouldShowOrientationOverlay: PropTypes.bool,
  };

  static defaultProps = {
    onNext: () => {
      NavigationManager.popToRoot();
    },
    onLearnMore: () => {
      /* */
    },
  };

  constructor(props: unknown) {
    super(props);

    this.styles = makeStyle(this.props.body);
    this.handleLearnMore = this._handleLearnMore.bind(this);
    this.handleNext = this._handleNext.bind(this);

    this.state = {
      shouldPlayConfetti: false,
      shouldRender: false,
    };
    this._waitForOrientationChange();
  }

  componentDidMount() {
    NavigationManager.closeSideMenu();
    GlobalEventEmitter.emit(SKZConstants.IS_HOME_SCREEN_FROM_PLAYER_LEVEL_UP_MODAL_OR_AD, true);
    if (Platform.OS == 'android') {
      this._backHandler = BackHandler.addEventListener('hardwareBackPress', (): boolean => {
        this.handleNext();
        return true;
      });
    }
  }

  componentWillUnmount() {
    if (this._backHandler) {
      this._backHandler.remove();
    }
  }

  async _waitForOrientationChange(): Promise<void> {
    await OrientationTransitionController.updateOrientationToPortrait();
    this._allowTransitionToFinish();
  }

  _setShouldPlayConfetti() {
    if (Platform.OS === 'android') {
      setTimeout(() => {
        this.setState({
          shouldPlayConfetti: true,
        });
      }, 300); // eslint-disable-line no-magic-numbers
    } else {
      this.setState({
        shouldPlayConfetti: true,
      });
    }
  }

  _allowTransitionToFinish() {
    setTimeout(() => {
      this.setState({
        shouldRender: true,
      });
    }, 500); // eslint-disable-line no-magic-numbers
  }

  _handleLearnMore() {
    this.props.onLearnMore();
    this._turnOffConfetti();
  }

  _handleNext() {
    this.props.onNext();
    this._turnOffConfetti();
  }

  _turnOffConfetti() {
    if (Platform.OS === 'android') {
      this.setState({
        shouldPlayConfetti: false,
      });
    }
  }

  _shouldPlayConfetti(): boolean {
    if (Platform.OS === 'android') {
      return this.state.shouldPlayConfetti && this.props.shouldPlayConfettiOnAndroid;
    }
    return this.state.shouldPlayConfetti;
  }

  _renderLevelProgress(): React.ReactNode {
    const circleProgressLevelSize = Sizes.isTablet() ? TABLET_SIZE : null;

    let levelTestID = PLAYER_LEVEL_UP_TEST_IDS.UNDEFINED_LEVEL;
    if (this.props.level.isSkillzLevel()) {
      levelTestID = PLAYER_LEVEL_UP_TEST_IDS.SKILLZ_LEVEL;
    } else if (this.props.level.isCurrentGameLevel()) {
      levelTestID = PLAYER_LEVEL_UP_TEST_IDS.GAME_LEVEL;
    }

    if (this.state.shouldPlayConfetti) {
      const newLevel = this.props.level;
      if (!this.props.level.isGrandFathered()) {
        newLevel.last_seen_level = newLevel.current_level - 1;
      }
      return (
        <View style={this.styles.progressContainer}>
          <CircleProgressLevel
            gameLevelData={newLevel}
            isLevelUpModal={true}
            levelTestID={levelTestID}
            playConfetti={() => {
              this._setShouldPlayConfetti();
            }}
            size={circleProgressLevelSize}
          />
        </View>
      );
    }
    return (
      <View style={this.styles.progressContainer}>
        <CircleProgressLevel
          gameLevelData={this.props.level}
          isLevelUpModal={true}
          levelTestID={levelTestID}
          playConfetti={() => {
            this._setShouldPlayConfetti();
          }}
          size={circleProgressLevelSize}
        />
      </View>
    );
  }

  _renderSubtitle(): React.ReactNode {
    const subtitleStyle = Sizes.isLandscape()
      ? this.styles.subtitleLandscape
      : this.styles.subtitle;
    return (
      <Label testID={PLAYER_LEVEL_UP_TEST_IDS.TITLE} style={subtitleStyle}>
        {this.props.subtitle}
      </Label>
    );
  }

  _renderBody(): Array<React.ReactNode> {
    const dividerStyle = Sizes.isLandscape() ? this.styles.dividerLandscape : this.styles.divider;
    const bodyTextStyle = Sizes.isLandscape()
      ? this.styles.bodyTextLandscape
      : this.styles.bodyText;
    if (!this.props.body) {
      return null;
    }
    return [
      <View testID={PLAYER_LEVEL_UP_TEST_IDS.BODY} style={dividerStyle} key={'levelup_divider'} />,
      <Label
        adjustsFontSizeToFit={true}
        numberOfLines={3}
        style={bodyTextStyle}
        key={'levelup_body'}
      >
        {this.props.body}
      </Label>,
    ];
  }

  _renderButtons(): React.ReactNode {
    const buttonStyle =
      this.props.body || this.props.showLearnMore
        ? this.styles.compactButton
        : this.styles.largeButton;
    const buttonTextStyle =
      this.props.body || this.props.showLearnMore
        ? this.styles.compactButtonText
        : this.styles.largeButtonText;
    return (
      <View style={this.styles.buttonContainer}>
        <Optional test={this.props.showLearnMore}>
          <PushButton
            colors={btnGradientTwo}
            testID={PLAYER_LEVEL_UP_TEST_IDS.LEARN_MORE}
            cornerRadius={viewCornerRadiusPrimary.borderRadius}
            onPress={this.handleLearnMore}
            style={buttonStyle}
            textStyle={buttonTextStyle}
            title={_m('LEARN MORE')}
          />
        </Optional>
        <PushButton
          colors={btnGradientOne}
          testID={PLAYER_LEVEL_UP_TEST_IDS.NEXT}
          cornerRadius={viewCornerRadiusPrimary.borderRadius}
          onPress={this.handleNext}
          style={buttonStyle}
          textStyle={buttonTextStyle}
          title={_m('NEXT')}
        />
      </View>
    );
  }

  _renderSplitLayout(): React.ReactNode {
    return (
      <View style={this.styles.contentContainerLandscape}>
        <View style={this.styles.leftPane}>
          {this._renderLevelProgress()}
          {this._renderSubtitle()}
        </View>
        <View style={this.styles.verticalDivider} />
        <View style={this.styles.rightPane}>
          <View style={this.styles.pad} />
          {this._renderBody()}
          <View style={this.styles.pad} />
          {this._renderButtons()}
        </View>
      </View>
    );
  }

  _renderModalContent(): React.ReactNode {
    if (this.props.body && Sizes.isLandscape()) {
      return this._renderSplitLayout();
    }
    const contentContainerStyle = Sizes.isLandscape()
      ? this.styles.contentContainerLandscape
      : this.styles.contentContainer;
    return (
      <View style={contentContainerStyle}>
        <View />
        {this._renderLevelProgress()}
        {this._renderSubtitle()}
        {this._renderBody()}
        {this._renderButtons()}
      </View>
    );
  }

  _renderPlayerLevelUpModal(): React.ReactNode {
    const modalStyle = Sizes.isLandscape()
      ? this.styles.modalStyleLandscape
      : this.styles.modalStyle;
    if (!this.state.shouldRender) {
      return false;
    }
    return (
      <HeaderModal
        onDone={this.props.onNext}
        showShadow={false}
        style={modalStyle}
        title={this.props.title}
        displayConfettiFrontShadow={this._shouldPlayConfetti()}
      >
        {this._renderModalContent()}
      </HeaderModal>
    );
  }

  render(): React.ReactNode {
    if (!this.state.shouldRender) {
      return false;
    }
    if (
      this.props.shouldShowOrientationOverlay &&
      OrientationTransitionController.getPreviousOrientation() == SKZConstants.SKZ_LANDSCAPE
    ) {
      return OrientationTransitionController.applyOrientationTransitionOverlay(
        this._renderPlayerLevelUpModal()
      );
    }
    return this._renderPlayerLevelUpModal();
  }
}
